import { FirestoreDocumentReference, useUpflowyStore } from '@upflowy/firestore';
import { ExperienceTypeVersionDoc, helper, LayoutVersionDoc, StepDoc } from '@upflowy/types';
import clsx from 'clsx';
import { collection, doc, limit, query, where } from 'firebase/firestore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import FlowStep from './FlowStep';

import { fetchDoc, fetchDocs } from '../helpers/fetch-documents';
import useDocCache from '../hooks/useDocCache';
import { useRedirect } from '../hooks/useRedirect';
import { useEventListener } from '../providers/event-listener-provider';
import { useNetworkErrorHandler } from '../providers/network-error-handler-provider';
import { useStateMachine } from '../providers/state-machine-provider';
import { onFlowReady } from '../services/upflowy-plugin';
import { StateEvent, StateTypeMeta, StepAssetType } from '../types';

type RefType<T> = {
  ref: FirestoreDocumentReference<T>;
};

type StateViewProps = {
  stepsRef: { id: (id: string) => RefType<StepDoc> };
};

export function StateView({ stepsRef }: StateViewProps) {
  const { state, busy, restoreSubmits } = useStateMachine();
  const [lastStep, setLastStep] = useState<StepDoc>();
  const [lastClickId, setLastClickId] = useState('');
  const { networkErrorHandler, retryCount } = useNetworkErrorHandler();
  const handleRedirect = useRedirect();

  const firestore = useUpflowyStore();
  const [experienceType, setExperienceType] = useState<
    | (ExperienceTypeVersionDoc & {
        type?: string;
      })
    | null
  >();
  const { getDocument } = useDocCache();
  const [layoutVersion, setLayoutVersion] = useState<LayoutVersionDoc | null>();
  const [networkError, setNetworkError] = useState(false);
  const { sendEvent } = useEventListener();
  const [assets, setAssets] = useState<StepAssetType[]>();

  const retrievedLayoutVersion = useMemo(() => layoutVersion?.id, [layoutVersion]);
  const retrievedExperienceType = useMemo(() => experienceType?.type, [experienceType]);
  const retrievedLastStep = useMemo(() => lastStep?.id, [lastStep]);

  useEffect(() => {
    lastStep && onFlowReady(lastStep.id);
  }, [lastStep]);

  useEffect(() => {
    if (restoreSubmits === true) {
      setLastClickId('');
    }
  }, [restoreSubmits]);

  useEffect(() => {
    if (
      !lastStep ||
      !lastStep.layoutVersionRef ||
      lastStep.layoutVersionRef === retrievedLayoutVersion
    ) {
      return;
    }
    getDocument<LayoutVersionDoc>(lastStep.layoutVersionRef)
      .then((l) => {
        setLayoutVersion(l);
      })
      .catch((err) => {
        networkErrorHandler(err, () => {
          setNetworkError(!retrievedLayoutVersion);
        });
      });
  }, [getDocument, lastStep, networkErrorHandler, retrievedLayoutVersion, retryCount]);

  useEffect(() => {
    const meta = state ? (Object.values(state?.meta)[0] as StateTypeMeta) : undefined;

    if (
      !meta?.stepId ||
      (lastStep && meta.stepId === lastStep.id) ||
      meta.stepId === retrievedLastStep
    ) {
      return;
    }

    const { stepId } = meta;
    const getStep = async () => {
      const step = await fetchDoc(stepsRef.id(stepId).ref);
      setLastStep(step.data());
    };
    getStep().catch((err) => {
      networkErrorHandler(err, () => {
        setNetworkError(!retrievedLastStep);
      });
    });
  }, [lastStep, state, stepsRef, retryCount, networkErrorHandler, retrievedLastStep]);

  useEffect(() => {
    const typeVersionRef = lastStep?.typeVersionRef;
    const type = lastStep?.type;

    if (type === retrievedExperienceType) {
      return;
    }
    const getExperienceType = async () => {
      let et = null;
      if (typeVersionRef) {
        et = (await fetchDoc(doc(firestore, typeVersionRef))).data() as ExperienceTypeVersionDoc;
      } else if (type) {
        // For backward compatibility
        const experienceTypeVersions = await fetchDocs(
          query(
            collection(firestore, 'ExperienceTypes', type.toLowerCase(), 'versions'),
            where('active', '==', true),
            limit(1),
          ),
        );
        et =
          experienceTypeVersions.docs.length > 0
            ? (experienceTypeVersions.docs[0].data() as ExperienceTypeVersionDoc)
            : null;
      }
      if (!et) {
        return;
      }
      setExperienceType(et);
    };

    getExperienceType().catch((err) => {
      networkErrorHandler(err, () => {
        setNetworkError(!retrievedExperienceType);
      });
    });
  }, [
    firestore,
    lastStep?.type,
    lastStep?.typeVersionRef,
    networkErrorHandler,
    retrievedExperienceType,
    retryCount,
  ]);

  useEffect(() => {
    if (lastStep?.css && experienceType?.css && layoutVersion?.css) {
      const assetList = [
        {
          ...layoutVersion,
          assetType: 'layout',
        },
        {
          ...experienceType,
          assetType: 'experience',
        },
      ]
        .filter(helper.isNotUndefined)
        .map((a) => {
          // eslint-disable-next-line max-len
          if ('css' in a && a.css)
            return {
              id: a.id,
              type: 'css-inline' as const,
              css: a.css,
              assetType: a.assetType,
            };
          if ('type' in a && a.type === ('css' as const)) {
            return {
              id: a.id,
              type: a.type,
              url: a.url,
              assetType: a.assetType,
            };
          }
          return undefined;
        })
        .filter(helper.isNotUndefined) as unknown as StepAssetType[];

      if ('css' in lastStep && lastStep.css) {
        assetList.push({
          id: lastStep.id,
          type: 'css-inline' as const,
          css: lastStep.css,
          assetType: 'step',
        });
      }

      setAssets(assetList);
    } else setAssets([]);
  }, [lastStep, experienceType, layoutVersion]);

  const stateEventHandler = useCallback(
    (stateEvent: StateEvent) => {
      // if the last submitted click was from an already seen id, then we block
      // sending the event so that we don't skip over steps or cause session complications
      if (lastClickId !== stateEvent.clickId) {
        sendEvent(stateEvent);
        setLastClickId(stateEvent.clickId || '');
      }
    },
    [sendEvent, lastClickId, setLastClickId],
  );

  useEffect(() => {
    if (networkError && !window.location.pathname.startsWith('/offline')) {
      handleRedirect(`/offline?returnTo=${window.location.pathname}`);
    }
  }, [networkError, handleRedirect]);

  return (
    <>
      <div className={clsx((busy || !assets) && 'is-submitting')}>
        {(!lastStep || !assets) && <div className="fullpage" />}
        {lastStep && state && assets && (
          <>
            <FlowStep
              step={lastStep}
              context={state.context ?? {}}
              state={state}
              visible
              stateEventCallback={stateEventHandler}
              assets={assets}
            />
          </>
        )}
      </div>
    </>
  );
}
